import { useEffect, useState } from 'react';
import { RichEditor, collectDocImageFileIds, applyResolvedUrlsToDoc } from '@odo/shared-ui';
import type { RichEditorImageUploadHandler, TiptapDoc } from '@odo/shared-ui';
import { resolveInternalImageUrls } from '../../../../services/internalImages';
import { getReasonDoc, getReasonText } from './inOfficeHoldNote';
import type { OriginEventRendererProps } from './types';

const NOOP_CHANGE = (): void => {};
const NOOP_UPLOAD: RichEditorImageUploadHandler = () => Promise.reject(new Error('read-only'));

/**
 * Hold-2 ("in-office step needed"): the note ODO sent the practice
 * explaining why the in-office appointment is needed. Rich text renders
 * read-only through the shared RichEditor (same pattern as NoteBody's
 * chart-note rendering); plain text falls back to a paragraph.
 */
export function InOfficeHoldNoteContent({ patientId, payload }: OriginEventRendererProps) {
    const reasonDoc = getReasonDoc(payload);
    const reasonText = getReasonText(payload);
    const [doc, setDoc] = useState<TiptapDoc | null>(reasonDoc);

    useEffect(() => {
        setDoc(reasonDoc);
        if (!reasonDoc) return;
        const fileIds = collectDocImageFileIds(reasonDoc);
        if (fileIds.length === 0) return;
        let cancelled = false;
        resolveInternalImageUrls(patientId, fileIds)
            .then((urls) => {
                if (!cancelled) setDoc(applyResolvedUrlsToDoc(reasonDoc, urls));
            })
            .catch(() => {
                /* leave unresolved — text stays intact, images may break */
            });
        return () => {
            cancelled = true;
        };
    }, [reasonDoc, patientId]);

    if (!doc && !reasonText) return null;

    if (doc) {
        return (
            <div className="odo-note-rich-body">
                <RichEditor
                    value={doc}
                    onChange={NOOP_CHANGE}
                    onImageUpload={NOOP_UPLOAD}
                    readOnly
                    chrome="bare"
                />
            </div>
        );
    }

    return <p className="text-sm text-amber-900 whitespace-pre-wrap">{reasonText}</p>;
}
